import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import Hidden from '@material-ui/core/Hidden';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Property from '@afosto/app-components-react/Property';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import Page from '@afosto/app-components-react/Page';
import ServicePreview from './ServiceCostsPreview';
import { services as servicesApi } from '@afosto/order/api/Services';
import camelize from '@afosto/utils/string/camelize';
import uuid from '@afosto/utils/uuid';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import translations from './translations';
import { valueFormatter, typeFormatter } from './formatters';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import formatCurrency from '@afosto/utils/formatCurrency';

const fetchServices = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => servicesApi.getServices(requestOptions),
    {
      transformData: data => data,
    },
  );

const ServicesContainer = props => <Grid container spacing={2} {...props} />;

const ServiceCosts = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);
  const history = useHistory();
  const intl = useIntl();
  const [isFetchingService, setIsFetchingService] = useState(false);
  const [service, setService] = useState(null);

  const dataSource = useDataGrid(fetchServices, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    pagination: {
      itemsPerPage: 100,
    },
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.serviceCosts.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'type',
          name: intl.formatMessage({
            id: 'dataGrid.columns.serviceCosts.type',
            defaultMessage: 'Type',
            description: 'The column name for column type',
          }),
          type: 'type',
        },
        {
          key: 'priority',
          name: intl.formatMessage({
            id: 'dataGrid.columns.serviceCosts.priority',
            defaultMessage: 'Priority',
            description: 'The column name for column priority',
          }),
        },
        {
          key: 'amount',
          name: intl.formatMessage({
            id: 'dataGrid.columns.serviceCosts.value',
            defaultMessage: 'Value',
            description: 'The column name for column amount',
          }),
          type: 'value',
        },
        {
          key: 'active_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.serviceCosts.activeAt',
            defaultMessage: 'Active at',
            description: 'The column name for column active at',
          }),
          type: 'datetime',
        },
        {
          key: 'expires_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.serviceCosts.expiresAt',
            defaultMessage: 'Expires at',
            description: 'The column name for column expires at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  const renderServiceCost = serviceData => (
    <Grid item xs={12} key={serviceData?.id}>
      <Card>
        <CardActionArea onClick={() => setActiveItem(serviceData?.id)}>
          <CardContent>
            <Typography variant="subtitle1">{serviceData?.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage({
                id: `serviceCostForm.types.${camelize(serviceData?.type)}`,
                defaultMessage: serviceData?.type,
                description: 'label of the service cost type value',
              })}
            </Typography>
            <Grid container spacing={1} wrap="nowrap">
              <Grid item xs={4} sm={5}>
                <Property
                  label={intl.formatMessage({
                    id: 'dataGrid.columns.serviceCosts.activeAt',
                    defaultMessage: 'Active at',
                    description: 'The column name for column active at',
                  })}
                  value={formatDateTime(serviceData?.active_at)}
                />
              </Grid>
              <Grid item xs={4} sm={5}>
                <Property
                  label={intl.formatMessage({
                    id: 'dataGrid.columns.serviceCosts.expiresAt',
                    defaultMessage: 'Expires at',
                    description: 'The column name for column expires at',
                  })}
                  value={formatDateTime(serviceData?.expires_at)}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <Property
                  label={intl.formatMessage({
                    id: 'dataGrid.columns.serviceCosts.value',
                    defaultMessage: 'Value',
                    description: 'The column name for column value',
                  })}
                  value={
                    serviceData?.is_percentage
                      ? `${serviceData?.amount}%`
                      : formatCurrency(serviceData?.amount / 100)
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  const renderServiceCostSkeleton = key => (
    <Grid item xs={12} key={key}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1">
            <Skeleton variant="text" width="80%" />
          </Typography>
          <Typography variant="body2">
            <Skeleton variant="text" width="50%" />
          </Typography>
          <Grid container spacing={1} wrap="nowrap">
            <Grid item xs={4} sm={5}>
              <Property
                label={intl.formatMessage({
                  id: 'dataGrid.columns.serviceCosts.activeAt',
                  defaultMessage: 'Active at',
                  description: 'The column name for column active at',
                })}
                value={<Skeleton variant="text" width="80%" />}
              />
            </Grid>
            <Grid item xs={4} sm={5}>
              <Property
                label={intl.formatMessage({
                  id: 'dataGrid.columns.serviceCosts.expiresAt',
                  defaultMessage: 'Expires at',
                  description: 'The column name for column expires at',
                })}
                value={<Skeleton variant="text" width="80%" />}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Property
                label={intl.formatMessage({
                  id: 'dataGrid.columns.serviceCosts.value',
                  defaultMessage: 'Value',
                  description: 'The column name for column value',
                })}
                value={<Skeleton variant="text" width="70%" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  useEffect(() => {
    let isMounted = true;

    const fetchService = async id => {
      setIsFetchingService(true);

      try {
        const response = await servicesApi.getService(id);
        const { data = {} } = response;

        const formattedConstraints = (data?.constraints ?? []).reduce(
          (acc, constraint) => [...acc, { ...constraint, id: uuid() }],
          [],
        );

        const formattedFilters = (data?.filters ?? []).reduce(
          (acc, filter) => [...acc, { ...filter, id: uuid() }],
          [],
        );

        if (isMounted) {
          setService({ ...data, constraints: formattedConstraints, filters: formattedFilters });
        }
      } catch (error) {
        if (isMounted) {
          setService(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingService(false);
        }
      }
    };

    if (activeItem) {
      fetchService(activeItem).catch(() => {
        // catch only.
      });
    } else {
      setService(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        fullWidth
        style={{ flexGrow: 1 }}
        primaryAction={{
          text: intl.formatMessage(translations.create.button),
          action: () => history.push('/service-costs/new'),
        }}
      >
        <Hidden implementation="js" mdUp>
          <DataList
            dataSource={dataSource}
            renderItem={renderServiceCost}
            renderSkeletonItem={renderServiceCostSkeleton}
            component={ServicesContainer}
          />
        </Hidden>
        <Hidden implementation="js" smDown>
          <DataGrid
            dataSource={dataSource}
            formatters={{ value: valueFormatter, type: typeFormatter }}
          />
        </Hidden>
      </Page>
      <ServicePreview
        isLoading={isFetchingService}
        isOpen={activeItem}
        onDeactivateService={() => {
          dataSource.reloadDataSource();
          setActiveItem(null);
          setService(null);
        }}
        onClose={() => setActiveItem(null)}
        service={service}
      />
    </Grid>
  );
};

export default ServiceCosts;
